import cn from 'classnames';
import React, { InputHTMLAttributes, useState, ReactNode } from 'react';
import { twMerge } from 'tailwind-merge';
import Loader from '../loader';

export interface Props extends InputHTMLAttributes<HTMLInputElement> {
  className?: string;
  inputClassName?: string;
  labelClassName?: string;
  label?: string;
  name: string;
  error?: string;
  type?: string;
  shadow?: boolean;
  variant?: 'normal' | 'solid' | 'outline' | 'line' | 'complementary';
  dimension?: 'small' | 'medium' | 'big' | 'md';
  isLoading?: boolean;
  adornment?: ReactNode;
}

const variantClasses = {
  normal:
    'bg-gray-100 border border-border-base focus:shadow focus:bg-light focus:border-accent',
  solid:
    'bg-gray-100 border border-border-100 focus:bg-light focus:border-accent',
  complementary:
    'bg-gray-100 border border-border-100 border-accent border-r-transparent rounded-tr-none rounded-br-none focus:bg-light focus:border-accent',
  outline: 'border border-border-base focus:border-accent',
  line: 'ltr:pl-0 rtl:pr-0 border-b border-border-base rounded-none focus:border-accent',
};

const sizeClasses = {
  small: 'text-sm h-[24px]',
  medium: 'h-12',
  md: 'h-9',
  big: 'h-14',
};

const Input = React.forwardRef<HTMLInputElement, Props>(
  (
    {
      className,
      label,
      name,
      error,
      adornment,
      variant = 'normal',
      dimension = 'medium',
      shadow = false,
      disabled = false,
      type = 'text',
      inputClassName,
      labelClassName = '',
      required = false,
      isLoading,
      ...rest
    },
    ref,
  ) => {
    const [touched, setTouched] = useState(false);

    return (
      <div className={twMerge(cn('relative', className))}>
        {label && (
          <label
            htmlFor={name}
            className={twMerge(
              'ml-4 mb-3 block text-sm font-semibold leading-none',
              touched && !error ? '!text-accent' : '',
              error ? 'text-red-500' : 'text-gray-200',
              labelClassName,
            )}
          >
            {label} {required && '*'}
          </label>
        )}
        <div className="relative flex items-center">
          {adornment && (
            <span className="absolute left-4 text-gray-500">{adornment}</span>
          )}
          <input
            id={name}
            name={name}
            type={type}
            ref={ref}
            className={twMerge(
              cn(
                'flex w-full appearance-none items-center rounded-3xl px-4 text-sm text-gray-200 transition duration-300 ease-in-out focus:outline-0 focus:ring-0',
                shadow && 'focus:shadow',
                variantClasses[variant],
                sizeClasses[dimension],
                disabled && 'cursor-not-allowed bg-gray-100',
                adornment ? 'pl-12' : '',
                inputClassName,
              ),
            )}
            required={required}
            disabled={disabled}
            autoComplete="off"
            autoCorrect="off"
            autoCapitalize="off"
            spellCheck="false"
            aria-invalid={error ? 'true' : 'false'}
            onFocus={() => setTouched(true)}
            {...rest}
          />
          {isLoading && (
            <div className="absolute w-4 h-4 right-3 top-1/2 transform -translate-y-1/2">
              <Loader size="sm" />
            </div>
          )}
        </div>
        {error && <p className="mt-2 text-xs text-red-500">{error}</p>}
      </div>
    );
  },
);
Input.displayName = 'Input';
export default Input;
